import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programsService from "../services/programs";
import * as programTestsService from "../services/programTests";
import "./Dashboard.css";
import "./Tests.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeProgramLevels(levels) {
  const raw = Array.isArray(levels) ? levels : [];
  const normalized = [];
  const seen = new Set();

  raw.forEach((item) => {
    if (item == null) return;
    if (typeof item === "string") {
      const grade = String(item).trim();
      if (!grade || seen.has(grade)) return;
      seen.add(grade);
      normalized.push({ grade, label: "" });
      return;
    }

    if (typeof item === "object") {
      const grade = String(item.grade ?? item.id ?? "").trim();
      if (!grade || seen.has(grade)) return;
      seen.add(grade);
      normalized.push({
        grade,
        label: String(item.label ?? item.title ?? "").trim(),
      });
    }
  });

  return normalized;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function formatShortDate(value, language) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language || "en", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

function DonutChart({ value, total, label, color = "#1e64da" }) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeValue = clamp(Number(value) || 0, 0, safeTotal || 0);
  const pct = safeTotal ? safeValue / safeTotal : 0;

  const size = 120;
  const stroke = 14;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * pct;
  const gap = circumference - dash;

  return (
    <div className="tests-dashboard__donut" aria-label={label} role="img">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(15, 35, 78, 0.10)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${gap}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="tests-dashboard__donutCenter">
        <div className="tests-dashboard__donutValue">
          {Math.round(pct * 100)}%
        </div>
        <div className="tests-dashboard__donutLabel">{label}</div>
      </div>
    </div>
  );
}

function Sparkline({ points }) {
  const safe = Array.isArray(points) ? points.map((n) => Number(n) || 0) : [];
  const w = 220;
  const h = 56;
  if (!safe.length) {
    return <div className="tests-dashboard__sparkEmpty" />;
  }
  const max = Math.max(...safe, 1);
  const min = Math.min(...safe, 0);
  const spread = Math.max(1, max - min);
  const step = safe.length > 1 ? w / (safe.length - 1) : w;
  const d = safe
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / spread) * (h - 6) - 3;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      className="tests-dashboard__spark"
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
    >
      <polyline
        fill="none"
        stroke="rgba(30, 100, 218, 0.25)"
        strokeWidth="6"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={d}
      />
      <polyline
        fill="none"
        stroke="#1e64da"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={d}
      />
    </svg>
  );
}

export default function Tests() {
  const { user, accessToken, effectiveRole } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const location = useLocation();
  const navigate = useNavigate();

  const isTeacher = effectiveRole === "teacher";
  const isAdmin = ["admin", "superadmin"].includes(effectiveRole);
  const isTeacherOrAdmin = isTeacher || isAdmin;
  const isStudent = effectiveRole === "student";

  const query = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );

  const initialProgramFromQuery = String(query.get("program") || "").trim();
  const initialLevelFromQuery = String(
    query.get("level") || query.get("level_grade") || "",
  ).trim();

  const [programs, setPrograms] = useState([]);
  const [programLoading, setProgramLoading] = useState(true);

  const [filters, setFilters] = useState({
    programSlug: initialProgramFromQuery,
    levelGrade: initialLevelFromQuery,
  });

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [onlyNeedsReview, setOnlyNeedsReview] = useState(false);

  const [createForm, setCreateForm] = useState({
    programSlug: "",
    levelGrade: "",
    testType: "mixed_test",
    title: "",
    description: "",
    durationMinutes: "",
  });
  const [createBusy, setCreateBusy] = useState(false);

  useEffect(() => {
    let ignore = false;

    async function loadPrograms() {
      setProgramLoading(true);
      try {
        const payload = await programsService.fetchPrograms({
          accessToken: user ? accessToken : undefined,
        });
        if (ignore) return;
        setPrograms(asArray(payload?.programs));
      } catch (e) {
        if (ignore) return;
        setPrograms([]);
      } finally {
        if (!ignore) setProgramLoading(false);
      }
    }

    loadPrograms();

    return () => {
      ignore = true;
    };
  }, [accessToken, user]);

  useEffect(() => {
    let ignore = false;

    async function loadTests() {
      setLoading(true);
      setError("");

      try {
        let params = undefined;
        let token = user ? accessToken : undefined;

        // Teacher/Admin dashboard wants stats.
        if (isTeacherOrAdmin) {
          params = { include_stats: "1" };
        }

        const payload = await programTestsService.fetchProgramTests({
          accessToken: token,
          params,
        });
        if (ignore) return;
        setTests(asArray(payload?.tests));
      } catch (e) {
        if (ignore) return;
        setTests([]);
        setError(
          e?.message ||
            t("testsModule.dashboard.failedLoadTests", {
              defaultValue: "Failed to load tests",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadTests();
    return () => {
      ignore = true;
    };
  }, [
    accessToken,
    filters.levelGrade,
    filters.programSlug,
    isStudent,
    isTeacherOrAdmin,
    t,
    user,
  ]);

  const handleUpdateFilter = (next) => {
    setFilters((prev) => ({ ...prev, ...next }));

    const nextParams = new URLSearchParams(location.search || "");
    if (typeof next.programSlug !== "undefined") {
      if (next.programSlug) nextParams.set("program", next.programSlug);
      else nextParams.delete("program");
    }
    if (typeof next.levelGrade !== "undefined") {
      if (next.levelGrade) nextParams.set("level", next.levelGrade);
      else nextParams.delete("level");
    }
    navigate(
      {
        pathname: "/tests",
        search: nextParams.toString() ? `?${nextParams}` : "",
      },
      { replace: true },
    );
  };

  const doCreate = async (event) => {
    event.preventDefault();
    if (!accessToken) return;

    const payload = {
      program: String(createForm.programSlug || "").trim(),
      level_grade: String(createForm.levelGrade || "").trim(),
      test_type:
        String(createForm.testType || "mixed_test").trim() || "mixed_test",
      title: String(createForm.title || "").trim(),
      description: String(createForm.description || "").trim(),
    };

    const minutesRaw = String(createForm.durationMinutes || "").trim();
    if (minutesRaw) {
      const value = Number(minutesRaw);
      if (!Number.isNaN(value)) {
        payload.duration_minutes = value;
      }
    }

    setCreateBusy(true);
    try {
      const created = await programTestsService.createProgramTest({
        accessToken,
        payload,
      });

      if (created?.id) {
        try {
          await programTestsService.createScaffoldExercises({
            accessToken,
            testId: created.id,
            testType: payload.test_type,
            startOrder: 0,
          });
        } catch {
          // best-effort
        }
      }
      navigate(`/tests/${created.id}/edit`);
    } catch (e) {
      setError(
        e?.message ||
          t("testsModule.newTest.failedCreate", {
            defaultValue: "Failed to create test",
          }),
      );
    } finally {
      setCreateBusy(false);
    }
  };

  const filterLevelOptions = useMemo(() => {
    const seen = new Map();
    const add = (grade, label = "") => {
      const key = String(grade || "").trim();
      if (key && !seen.has(key)) seen.set(key, { grade: key, label });
    };
    const programsInScope = filters.programSlug
      ? programs.filter((p) => p.slug === filters.programSlug)
      : programs;
    programsInScope.forEach((p) =>
      normalizeProgramLevels(p?.levels).forEach((lvl) => add(lvl.grade, lvl.label)),
    );
    asArray(tests)
      .filter(
        (test) =>
          !filters.programSlug ||
          String(test?.program_slug || "").trim() === filters.programSlug,
      )
      .forEach((test) => add(test?.level_grade));
    return [...seen.values()];
  }, [filters.programSlug, programs, tests]);

  const programTitle = (slug) =>
    programs.find((p) => p.slug === slug)?.title || slug;

  const visibleTests = useMemo(() => {
    let list = asArray(tests);

    if (filters.programSlug) {
      list = list.filter(
        (t) => String(t?.program_slug || "").trim() === filters.programSlug,
      );
    }
    if (filters.levelGrade) {
      list = list.filter(
        (t) => String(t?.level_grade || "").trim() === filters.levelGrade,
      );
    }
    if (onlyNeedsReview) {
      list = list.filter(
        (t) => Number(t?.unreviewed_submissions_count ?? 0) > 0,
      );
    }

    return list;
  }, [filters.levelGrade, filters.programSlug, onlyNeedsReview, tests]);

  const visibleStats = useMemo(() => {
    const list = asArray(visibleTests);
    return {
      total: list.length,
      published: list.filter((x) => x?.is_published).length,
      drafts: list.filter((x) => !x?.is_published).length,
      submissions: list.reduce(
        (acc, x) => acc + Number(x?.submissions_count ?? 0),
        0,
      ),
      unreviewed: list.reduce(
        (acc, x) => acc + Number(x?.unreviewed_submissions_count ?? 0),
        0,
      ),
    };
  }, [visibleTests]);

  const reviewLoad = useMemo(() => {
    const list = asArray(visibleTests)
      .map((t) => ({
        id: t.id,
        title: String(t?.title || "").trim(),
        unreviewed: Number(t?.unreviewed_submissions_count ?? 0),
      }))
      .filter((x) => x.unreviewed > 0)
      .sort((a, b) => b.unreviewed - a.unreviewed)
      .slice(0, 6);
    const max = Math.max(0, ...list.map((x) => x.unreviewed));
    return { list, max: max || 1 };
  }, [visibleTests]);

  const needsReviewList = useMemo(() => {
    return asArray(tests)
      .filter((t) => Number(t?.unreviewed_submissions_count ?? 0) > 0)
      .sort((a, b) => {
        const ua = Number(a?.unreviewed_submissions_count ?? 0);
        const ub = Number(b?.unreviewed_submissions_count ?? 0);
        if (ub !== ua) return ub - ua;
        return String(b?.updated_at || "").localeCompare(
          String(a?.updated_at || ""),
        );
      })
      .slice(0, 5);
  }, [tests]);

  const recentTests = useMemo(() => {
    return asArray(tests)
      .slice()
      .sort((a, b) =>
        String(b?.updated_at || "").localeCompare(String(a?.updated_at || "")),
      )
      .slice(0, 8);
  }, [tests]);

  const testsCreatedSpark = useMemo(() => {
    const list = asArray(tests)
      .map((t) => ({
        created_at: t?.created_at,
      }))
      .filter((x) => x.created_at);

    const days = 14;
    const now = new Date();
    const buckets = Array.from({ length: days }, () => 0);

    list.forEach((t) => {
      const dt = new Date(t.created_at);
      if (Number.isNaN(dt.getTime())) return;
      const diffDays = Math.floor((now - dt) / (24 * 60 * 60 * 1000));
      if (diffDays < 0 || diffDays >= days) return;
      buckets[days - 1 - diffDays] += 1;
    });

    return buckets;
  }, [tests]);

  if (!isTeacherOrAdmin) {
    return <Navigate to="/placement-test" replace />;
  }

  const doTogglePublish = async (test) => {
    if (!accessToken) return;
    try {
      const updated = await programTestsService.updateProgramTest({
        accessToken,
        testId: test.id,
        payload: { is_published: !test.is_published },
      });
      setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (e) {
      setError(e?.message || t("testsModule.dashboard.failedUpdate"));
    }
  };

  const doDelete = async (test) => {
    if (!accessToken) return;
    if (!window.confirm(t("testsModule.dashboard.confirmDelete"))) return;

    try {
      await programTestsService.deleteProgramTest({
        accessToken,
        testId: test.id,
      });
      setTests((prev) => prev.filter((t) => t.id !== test.id));
    } catch (e) {
      setError(e?.message || t("testsModule.dashboard.failedDelete"));
    }
  };

  return (
    <div className="dashboard-page tests-dashboard" dir={dir}>
      <div className="dashboard-shell">
        <section className="dashboard-hero">
          <div className="dashboard-hero__copy">
            <p className="dashboard-hero__role">
              {isAdmin
                ? t("testsModule.dashboard.roleAdmin")
                : t("testsModule.dashboard.roleTeacher")}
            </p>
            <h1>{t("testsModule.dashboard.title")}</h1>
            <p>{t("testsModule.dashboard.subtitle")}</p>
            <div className="dashboard-actions">
              <Link to="/placement-test" className="btn ghost">
                {t("testsModule.dashboard.previewPlacement")}
              </Link>
            </div>
          </div>

          <div className="dashboard-hero__card">
            <h3>{t("testsModule.dashboard.createCardTitle")}</h3>
            {!programLoading && !programs.length ? (
              // Teachers can only create tests for programs an admin assigned to them.
              <p className="tests-page__notice" role="status">
                <i className="fa-solid fa-circle-info" aria-hidden="true" />{" "}
                {t("testsModule.dashboard.noProgramsAssigned")}
              </p>
            ) : null}
            <form className="tests-page__form" onSubmit={doCreate}>
              <label>
                <span>{t("testsModule.common.program")}</span>
                <select
                  value={createForm.programSlug}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      programSlug: e.target.value,
                    }))
                  }
                  disabled={createBusy || programLoading}
                  required
                >
                  <option value="">{t("testsModule.common.select")}</option>
                  {programs.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.title || p.slug}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>{t("testsModule.common.level")}</span>
                <input
                  value={createForm.levelGrade}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, levelGrade: e.target.value }))
                  }
                  disabled={createBusy}
                  placeholder={t("testsModule.dashboard.placeholderLevel")}
                  list="tests-level-suggestions"
                  required
                />
              </label>

              <label>
                <span>
                  {t("testsModule.editor.testType", {
                    defaultValue: "Test type",
                  })}
                </span>
                <select
                  value={createForm.testType}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, testType: e.target.value }))
                  }
                  disabled={createBusy}
                >
                  <option value="text_comprehension">
                    {t("testsModule.testTypes.text_comprehension", {
                      defaultValue: "Text Comprehension",
                    })}
                  </option>
                  <option value="grammar">
                    {t("testsModule.testTypes.grammar", {
                      defaultValue: "Grammar",
                    })}
                  </option>
                  <option value="vocabulary">
                    {t("testsModule.testTypes.vocabulary", {
                      defaultValue: "Vocabulary",
                    })}
                  </option>
                  <option value="math_problems">
                    {t("testsModule.testTypes.math_problems", {
                      defaultValue: "Math Problems",
                    })}
                  </option>
                  <option value="calculations">
                    {t("testsModule.testTypes.calculations", {
                      defaultValue: "Calculations",
                    })}
                  </option>
                  <option value="qcm">
                    {t("testsModule.testTypes.qcm", { defaultValue: "QCM" })}
                  </option>
                  <option value="problem_solving">
                    {t("testsModule.testTypes.problem_solving", {
                      defaultValue: "Problem Solving",
                    })}
                  </option>
                  <option value="data_interpretation">
                    {t("testsModule.testTypes.data_interpretation", {
                      defaultValue: "Data Interpretation",
                    })}
                  </option>
                  <option value="written_expression">
                    {t("testsModule.testTypes.written_expression", {
                      defaultValue: "Written Expression",
                    })}
                  </option>
                  <option value="mixed_test">
                    {t("testsModule.testTypes.mixed_test", {
                      defaultValue: "Mixed Test",
                    })}
                  </option>
                </select>
              </label>

              <label className="tests-page__full">
                <span>{t("testsModule.common.title")}</span>
                <input
                  value={createForm.title}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  disabled={createBusy}
                  required
                />
              </label>

              <label className="tests-page__full">
                <span>{t("testsModule.common.description")}</span>
                <textarea
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      description: e.target.value,
                    }))
                  }
                  disabled={createBusy}
                  rows={3}
                />
              </label>

              <label>
                <span>{t("testsModule.common.durationMinutes")}</span>
                <input
                  value={createForm.durationMinutes}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      durationMinutes: e.target.value,
                    }))
                  }
                  disabled={createBusy}
                  type="number"
                  min="1"
                />
              </label>

              <div className="tests-page__actions">
                <button
                  className="btn primary"
                  type="submit"
                  disabled={createBusy}
                >
                  {createBusy
                    ? t("testsModule.dashboard.creating")
                    : t("testsModule.dashboard.create")}
                </button>
              </div>
            </form>
          </div>
        </section>

        {error ? (
          <div className="dashboard-error" role="alert">
            <p>{error}</p>
          </div>
        ) : null}

        <section className="dashboard-section">
          <div className="dashboard-cards">
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("testsModule.dashboard.stats.tests")}
              </p>
              <strong className="dashboard-card__value">
                {visibleStats.total}
              </strong>
              <span className="dashboard-card__hint">
                {t("testsModule.dashboard.stats.forCurrentFilters")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("testsModule.dashboard.stats.published")}
              </p>
              <strong className="dashboard-card__value">
                {visibleStats.published}
              </strong>
              <span className="dashboard-card__hint">
                {t("testsModule.dashboard.stats.visibleForStudents")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("testsModule.dashboard.stats.submissions")}
              </p>
              <strong className="dashboard-card__value">
                {visibleStats.submissions}
              </strong>
              <span className="dashboard-card__hint">
                {t("testsModule.dashboard.stats.totalSubmissions")}
              </span>
            </article>
            <article className="dashboard-card">
              <p className="dashboard-card__label">
                {t("testsModule.dashboard.stats.notReviewed")}
              </p>
              <strong className="dashboard-card__value">
                {visibleStats.unreviewed}
              </strong>
              <span className="dashboard-card__hint">
                {t("testsModule.dashboard.stats.needsReview")}
              </span>
            </article>
          </div>
        </section>

        <section className="dashboard-section">
          <header className="dashboard-section__head">
            <div>
              <h2>{t("testsModule.dashboard.insightsTitle")}</h2>
              <p>{t("testsModule.dashboard.insightsSubtitle")}</p>
            </div>
          </header>

          <div className="dashboard-cards">
            <article className="dashboard-card tests-dashboard__chartCard">
              <div className="tests-dashboard__chartHead">
                <div>
                  <p className="dashboard-card__label">
                    {t("testsModule.dashboard.publishRate")}
                  </p>
                  <span className="dashboard-card__hint">
                    {t("testsModule.dashboard.publishedVsDrafts")}
                  </span>
                </div>
              </div>
              <div className="tests-dashboard__chartBody">
                <DonutChart
                  value={visibleStats.published}
                  total={visibleStats.total}
                  label={t("testsModule.dashboard.stats.published")}
                  color="#1e64da"
                />
                <div className="tests-dashboard__legend">
                  <div className="tests-dashboard__legendRow">
                    <span
                      className="tests-dashboard__dot"
                      style={{ background: "#1e64da" }}
                    />
                    <span>
                      {t("testsModule.dashboard.stats.published")}:{" "}
                      {visibleStats.published}
                    </span>
                  </div>
                  <div className="tests-dashboard__legendRow">
                    <span
                      className="tests-dashboard__dot"
                      style={{ background: "rgba(15, 35, 78, 0.18)" }}
                    />
                    <span>
                      {t("testsModule.dashboard.drafts")}: {visibleStats.drafts}
                    </span>
                  </div>
                </div>
              </div>
            </article>

            <article className="dashboard-card tests-dashboard__chartCard">
              <div className="tests-dashboard__chartHead">
                <div>
                  <p className="dashboard-card__label">
                    {t("testsModule.dashboard.reviewLoad")}
                  </p>
                  <span className="dashboard-card__hint">
                    {t("testsModule.dashboard.topTestsNeedingReview")}
                  </span>
                </div>
              </div>
              <div className="tests-dashboard__bars">
                {reviewLoad.list.length ? (
                  reviewLoad.list.map((row) => (
                    <div className="tests-dashboard__barRow" key={row.id}>
                      <div className="tests-dashboard__barTop">
                        <span className="tests-dashboard__barLabel">
                          {row.title || `#${row.id}`}
                        </span>
                        <span className="tests-dashboard__barValue">
                          {row.unreviewed}
                        </span>
                      </div>
                      <div className="tests-dashboard__barTrack">
                        <div
                          className="tests-dashboard__barFill"
                          style={{
                            width: `${Math.round((row.unreviewed / reviewLoad.max) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="dashboard-muted">
                    {t("testsModule.dashboard.nothingToReview")}
                  </p>
                )}
              </div>
            </article>

            <article className="dashboard-card tests-dashboard__chartCard">
              <div className="tests-dashboard__chartHead">
                <div>
                  <p className="dashboard-card__label">
                    {t("testsModule.dashboard.testActivity")}
                  </p>
                  <span className="dashboard-card__hint">
                    {t("testsModule.dashboard.newTests14Days")}
                  </span>
                </div>
              </div>
              <div className="tests-dashboard__activity">
                <Sparkline points={testsCreatedSpark} />
                <div className="tests-dashboard__activityList">
                  {recentTests.slice(0, 4).map((test) => (
                    <div className="tests-dashboard__activityRow" key={test.id}>
                      <div className="tests-dashboard__activityTitle">
                        {test.title}
                      </div>
                      <div className="tests-dashboard__activityMeta">
                        {formatShortDate(test.updated_at, language)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </section>

        {needsReviewList.length ? (
          <section className="dashboard-section">
            <header className="dashboard-section__head">
              <div>
                <h2>{t("testsModule.dashboard.needsReviewTitle")}</h2>
                <p>{t("testsModule.dashboard.needsReviewSubtitle")}</p>
              </div>
            </header>

            <div className="dashboard-list">
              {needsReviewList.map((test) => (
                <article className="dashboard-list__item" key={test.id}>
                  <div>
                    <h3>{test.title}</h3>
                    <p className="dashboard-muted">
                      {t("testsModule.common.program")}:{" "}
                      {programTitle(test.program_slug)} •{" "}
                      {t("testsModule.common.level")}: {test.level_grade}
                    </p>
                  </div>
                  <div className="dashboard-list__meta">
                    <span>
                      {t("testsModule.dashboard.stats.notReviewed")}:{" "}
                      {Number(test.unreviewed_submissions_count ?? 0)}
                    </span>
                    <Link
                      className="btn primary"
                      to={`/tests/${test.id}/submissions`}
                    >
                      {t("testsModule.dashboard.startReview")}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="dashboard-section">
          <header className="dashboard-section__head">
            <div>
              <h2>{t("testsModule.dashboard.manageTitle")}</h2>
              <p>{t("testsModule.dashboard.manageSubtitle")}</p>
            </div>
            <div className="tests-dashboard__filters">
              <Link className="btn primary" to="/tests/new">
                {t("testsModule.dashboard.newTest")}
              </Link>

              <select
                value={filters.programSlug}
                onChange={(e) =>
                  handleUpdateFilter({ programSlug: e.target.value })
                }
                disabled={programLoading}
                aria-label={t("testsModule.dashboard.filterProgram")}
              >
                <option value="">
                  {t("testsModule.dashboard.allPrograms")}
                </option>
                {programs.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.title || p.slug}
                  </option>
                ))}
              </select>

              <select
                value={filters.levelGrade}
                onChange={(e) =>
                  handleUpdateFilter({ levelGrade: e.target.value })
                }
                disabled={programLoading}
                aria-label={t("testsModule.dashboard.filterLevel")}
              >
                <option value="">{t("testsModule.dashboard.allLevels")}</option>
                {filterLevelOptions.map((lvl) => (
                  <option key={lvl.grade} value={lvl.grade}>
                    {lvl.label ? `${lvl.grade} — ${lvl.label}` : lvl.grade}
                  </option>
                ))}
              </select>

              <label className="tests-dashboard__toggle">
                <input
                  type="checkbox"
                  checked={onlyNeedsReview}
                  onChange={(e) => setOnlyNeedsReview(e.target.checked)}
                />
                <span>{t("testsModule.dashboard.onlyNotReviewed")}</span>
              </label>
            </div>
          </header>

          {loading ? (
            <p className="dashboard-muted">{t("testsModule.common.loading")}</p>
          ) : null}

          {!loading && !visibleTests.length ? (
            <p className="dashboard-muted">
              {t("testsModule.dashboard.noTestsFound")}
            </p>
          ) : null}

          {!!visibleTests.length && (
            <div className="tests-cards">
              {visibleTests.map((test) => {
                const copies = Number(test.submissions_count ?? 0);
                const toReview = Number(test.unreviewed_submissions_count ?? 0);
                return (
                  <article className="tests-card" key={test.id}>
                    <div className="tests-card__main">
                      <div className="tests-card__chips">
                        <span
                          className={`tests-card__status ${
                            test.is_published ? "is-published" : "is-draft"
                          }`}
                        >
                          <i
                            className={`fa-solid ${
                              test.is_published ? "fa-circle-check" : "fa-pen-ruler"
                            }`}
                            aria-hidden="true"
                          />
                          {test.is_published
                            ? t("testsModule.dashboard.statusPublished")
                            : t("testsModule.dashboard.statusDraft")}
                        </span>
                        {test.level_grade ? (
                          <span className="tests-card__chip">
                            {t("testsModule.common.level")}: {test.level_grade}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="tests-card__title">
                        <Link to={`/tests/${test.id}/edit`}>{test.title}</Link>
                      </h3>
                      <p className="tests-card__meta">
                        {programTitle(test.program_slug)}
                        {test.duration_minutes
                          ? ` · ${test.duration_minutes} ${t("testsModule.take.minutesShort")}`
                          : ""}
                      </p>
                      {test.description ? (
                        <p className="tests-card__desc">{test.description}</p>
                      ) : null}
                    </div>

                    <Link
                      className={`tests-card__copies${toReview ? " has-pending" : ""}`}
                      to={`/tests/${test.id}/submissions`}
                    >
                      <strong>{copies}</strong>
                      <span>{t("testsModule.dashboard.stats.submissions")}</span>
                      {toReview ? (
                        <em>
                          {toReview} {t("testsModule.dashboard.stats.needsReview")}
                        </em>
                      ) : null}
                    </Link>

                    <div className="tests-card__actions">
                      <Link className="btn primary" to={`/tests/${test.id}/edit`}>
                        <i className="fa-solid fa-pen" aria-hidden="true" />{" "}
                        {t("testsModule.common.edit")}
                      </Link>
                      <Link className="btn ghost" to={`/tests/${test.id}`}>
                        <i className="fa-solid fa-eye" aria-hidden="true" />{" "}
                        {t("testsModule.common.preview")}
                      </Link>
                      <button
                        type="button"
                        className="tests-card__icon"
                        onClick={() => doTogglePublish(test)}
                        title={
                          test.is_published
                            ? t("testsModule.dashboard.unpublish")
                            : t("testsModule.dashboard.publish")
                        }
                        aria-label={
                          test.is_published
                            ? t("testsModule.dashboard.unpublish")
                            : t("testsModule.dashboard.publish")
                        }
                      >
                        <i
                          className={`fa-solid ${
                            test.is_published ? "fa-eye-slash" : "fa-paper-plane"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                      <button
                        type="button"
                        className="tests-card__icon tests-card__icon--danger"
                        onClick={() => doDelete(test)}
                        title={t("testsModule.common.delete")}
                        aria-label={t("testsModule.common.delete")}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
